import React from "react";
import { Col, Form, FormControlProps } from "react-bootstrap";
import {
  Control,
  FieldValues,
  RegisterOptions,
  useController,
  FieldPath,
  PathValue,
} from "react-hook-form";

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends FormControlProps {
  component?: React.FC;
  xs?: string | number;
  lg?: string | number;
  md?: string | number;
  sm?: string | number;
  label?: string;
  options: any[];
  className?: string;
  label_class?: string;
  group_class?: string;
  invalid?: string;
  loading?: boolean;
  selectOption?: string;
  children?: React.ReactElement;
  multiline?: boolean;
  name: TName | any;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, TName>;
  defaultValue?: PathValue<TFieldValues, TName>;
}

function Select<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  component,
  lg,
  md,
  xs,
  sm,
  label,
  loading,
  selectOption,
  label_class,
  group_class,
  className,
  multiline,
  options,
  name,
  control,
  children,
  rules,
  defaultValue,
  ...rest
}: Props<TFieldValues, TName>) {
  const {
    field,
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue: defaultValue,
  });

  return (
    <Form.Group
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      as={Col}
      className={group_class}
    >
      {label ? <Form.Label className={label_class}>{label}</Form.Label> : null}
      <Form.Control
        as="select"
        className={`w-100 ${className ? className : "dropdown-toggle"}`}
        {...rest}
        {...field}
        isInvalid={invalid}
      >
        {selectOption ? <option value="">{selectOption}</option> : null}

        {!loading
          ? options.map(
              (
                option: {
                  id: string | number;
                  name: string;
                },
                key: React.Key
              ) => (
                <option key={key} value={option.id}>
                  {option.name}
                </option>
              )
            )
          : null}
      </Form.Control>
      <Form.Control.Feedback type="invalid">
        {error?.message}
      </Form.Control.Feedback>

      {children ? children : null}
    </Form.Group>
  );
}

export default Select;
